import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SyncIcon from '@mui/icons-material/Sync';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import AuthContext from '../../context/authContext';
import useBranch from '../../hooks/useBranch';
import useRecruitee from '../../hooks/useRecruitee';
import useRole from '../../hooks/useRole';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

const APPLICANT_STATUS_OPTIONS = [
  { value: 'all', label: 'Alle Bewerber' },
  { value: 'overdue', label: 'Nur überfällig' },
  { value: 'new', label: 'Neu' },
  { value: 'qualified', label: 'Qualifiziert' },
  { value: 'disqualified', label: 'Disqualifiziert' },
];

const DEFAULT_FILTERS = {
  status: 'all',
  branchId: '',
  jobId: '',
  search: '',
};

function getBranchLabel(branch) {
  return branch?.longName || branch?.locationName || branch?.name || branch?._id || '';
}

function getRoleLabel(role) {
  return role?.readableName || role?.name || role?._id || '';
}

function formatDate(value) {
  return value ? dayjs(value).format('DD.MM.YYYY HH:mm') : '-';
}

function formatPhaseDuration(value) {
  if (!value) return '-';
  const startedAt = dayjs(value);
  if (!startedAt.isValid()) return '-';
  const now = dayjs();
  const days = now.diff(startedAt, 'day');
  if (days > 1) return `${days} Tage`;
  if (days === 1) return '1 Tag';
  const hours = now.diff(startedAt, 'hour');
  if (hours > 1) return `${hours} Stunden`;
  if (hours === 1) return '1 Stunde';
  return 'unter 1 Stunde';
}

function getPhaseStartedAt(applicant) {
  return applicant.placementUpdatedAt || applicant.appliedAt || applicant.candidateCreatedAt;
}

function statusChip(applicant) {
  if (applicant.isOverdue) {
    return <Chip size='small' color='error' label='Überfällig' />;
  }
  if (String(applicant.status).toLowerCase() === 'disqualified') {
    return <Chip size='small' color='default' label='Disqualifiziert' />;
  }
  if (String(applicant.status).toLowerCase() === 'qualified') {
    return <Chip size='small' color='success' label='Qualifiziert' />;
  }
  if (String(applicant.status).toLowerCase() === 'new') {
    return <Chip size='small' color='info' label='Neu' />;
  }
  return <Chip size='small' variant='outlined' label={applicant.status || 'Planmäßig'} />;
}

export default function RecruiteeManagement() {
  const authCtx = useContext(AuthContext);
  const hasFullRead = authCtx.checkRight('recruitee:read');
  const canRead =
    hasFullRead || authCtx.checkRight('recruitee:read:own');
  const canManage = authCtx.checkRight('recruitee:manage');
  const canSync = authCtx.checkRight('recruitee:sync');
  const canSeeMappings = hasFullRead;
  const assignedBranchCount = authCtx.currentUser?.assignedBranches?.length || 0;
  const hideBranchFilter = !hasFullRead && assignedBranchCount <= 1;
  const {
    listJobs,
    listMappings,
    createMapping,
    updateMapping,
    deleteMapping,
    sync,
    listApplicants,
    getApplicantStats,
  } = useRecruitee();
  const { getBranches } = useBranch();
  const { getRoles } = useRole();

  const [tab, setTab] = useState(0);
  const [branches, setBranches] = useState([]);
  const [roles, setRoles] = useState([]);
  const [collections, setCollections] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [applicants, setApplicants] = useState([]);
  const [applicantTotal, setApplicantTotal] = useState(0);
  const [stats, setStats] = useState({ total: 0, overdue: 0, stale: 0, byStatus: [] });
  const [loading, setLoading] = useState(true);
  const [applicantLoading, setApplicantLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMapping, setEditMapping] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  const loadApplicants = useCallback(
    async nextFilters => {
      const effectiveFilters = nextFilters || DEFAULT_FILTERS;
      setApplicantLoading(true);
      try {
        const params = {
          page: 1,
          limit: 100,
          branchId: effectiveFilters.branchId || undefined,
          jobId: effectiveFilters.jobId || undefined,
          search: effectiveFilters.search || undefined,
        };
        if (effectiveFilters.status === 'overdue') {
          params.overdue = true;
        } else if (effectiveFilters.status !== 'all') {
          params.status = effectiveFilters.status;
        }
        const [listRes, statsRes] = await Promise.all([
          listApplicants(params),
          getApplicantStats(params),
        ]);
        setApplicants(listRes?.items || []);
        setApplicantTotal(listRes?.total || 0);
        setStats(statsRes || { total: 0, overdue: 0, stale: 0, byStatus: [] });
      } finally {
        setApplicantLoading(false);
      }
    },
    [getApplicantStats, listApplicants]
  );

  const loadBaseData = useCallback(async () => {
    setLoading(true);
    try {
      const [jobRes, mappingRes, branchRes, roleRes] = await Promise.all([
        listJobs(),
        canSeeMappings ? listMappings() : Promise.resolve([]),
        getBranches(),
        getRoles(),
      ]);
      setCollections(jobRes?.collections || []);
      setJobs(jobRes?.jobs || []);
      setMappings(Array.isArray(mappingRes) ? mappingRes : []);
      setBranches(Array.isArray(branchRes) ? branchRes : []);
      setRoles(Array.isArray(roleRes) ? roleRes : roleRes?.roles || []);
      await loadApplicants();
    } finally {
      setLoading(false);
    }
  }, [canSeeMappings, getBranches, getRoles, listJobs, listMappings, loadApplicants]);

  useEffect(() => {
    if (!canRead) return;
    loadBaseData();
  }, [canRead]); // eslint-disable-line react-hooks/exhaustive-deps

  const overdueApplicants = useMemo(
    () => applicants.filter(applicant => applicant.isOverdue),
    [applicants]
  );

  const handleApplyFilters = async () => {
    await loadApplicants(filters);
  };

  const handleTabChange = (_, value) => {
    if (!canSeeMappings && value === 1) return;
    setTab(value);
  };

  const handleSync = async mappingId => {
    setSyncing(true);
    try {
      const result = await sync(mappingId ? { mappingId } : {});
      toast.success(
        mappingId
          ? 'Recruitee-Mapping synchronisiert'
          : `Recruitee synchronisiert (${result?.upserted || 0} Bewerber-Placements)`
      );
      await loadBaseData();
    } finally {
      setSyncing(false);
    }
  };

  const handleSaveMapping = async payload => {
    setSaving(true);
    try {
      if (editMapping) {
        await updateMapping(editMapping._id, payload);
      } else {
        await createMapping(payload);
      }
      toast.success('Recruitee-Mapping gespeichert');
      setDialogOpen(false);
      setEditMapping(null);
      await loadBaseData();
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMapping = async mapping => {
    if (!window.confirm(`Mapping für "${mapping.jobTitle}" wirklich löschen?`)) return;
    await deleteMapping(mapping._id);
    toast.success('Recruitee-Mapping gelöscht');
    await loadBaseData();
  };

  if (!canRead) return <Navigate to='/welcome' replace />;

  return (
    <Box sx={{ ...viewRootTextPrimarySx, p: 3, maxWidth: 1500, mx: 'auto' }}>
      {canSync && (
        <Stack direction='row' justifyContent='flex-end' sx={{ mb: 2 }}>
          <Button
            variant='contained'
            startIcon={syncing ? <CircularProgress size={16} /> : <SyncIcon />}
            onClick={() => handleSync()}
            disabled={syncing}
          >
            Recruitee synchronisieren
          </Button>
        </Stack>
      )}

      {stats.overdue > 0 && (
        <Alert severity='warning' sx={{ mb: 2 }}>
          {stats.overdue} Bewerber sind überfällig und benötigen Aufmerksamkeit.
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <StatsCard label='Bewerber' value={stats.total} />
        <StatsCard label='Überfällig' value={stats.overdue} color='error.main' />
        <StatsCard label='Geladene Ansicht' value={applicantTotal} />
        {canSeeMappings && <StatsCard label='Mappings' value={mappings.length} />}
      </Grid>

      {canSeeMappings && (
        <Paper sx={{ mb: 2 }}>
          <Tabs value={tab} onChange={handleTabChange} variant='scrollable'>
            <Tab label='Bewerberübersicht' />
            <Tab label='Job-Mappings' />
          </Tabs>
        </Paper>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : tab === 0 ? (
        <ApplicantsTab
          applicants={applicants}
          overdueApplicants={overdueApplicants}
          applicantLoading={applicantLoading}
          filters={filters}
          setFilters={setFilters}
          branches={branches}
          hideBranchFilter={hideBranchFilter}
          jobs={jobs}
          onApplyFilters={handleApplyFilters}
        />
      ) : (
        <MappingsTab
          mappings={mappings}
          canManage={canManage}
          canSync={canSync}
          syncing={syncing}
          onAdd={() => {
            setEditMapping(null);
            setDialogOpen(true);
          }}
          onEdit={mapping => {
            setEditMapping(mapping);
            setDialogOpen(true);
          }}
          onDelete={handleDeleteMapping}
          onSync={handleSync}
        />
      )}

      {dialogOpen && (
        <MappingDialog
          mapping={editMapping}
          branches={branches}
          roles={roles}
          collections={collections}
          jobs={jobs}
          saving={saving}
          onCancel={() => {
            setDialogOpen(false);
            setEditMapping(null);
          }}
          onSave={handleSaveMapping}
        />
      )}
    </Box>
  );
}

function StatsCard({ label, value, color }) {
  return (
    <Grid item xs={6} md={3}>
      <Card variant='outlined'>
        <CardContent>
          <Typography variant='overline' color={color || 'text.secondary'}>
            {label}
          </Typography>
          <Typography variant='h5' color={color || 'text.primary'}>
            {value}
          </Typography>
        </CardContent>
      </Card>
    </Grid>
  );
}

function ApplicantsTab({
  applicants,
  overdueApplicants,
  applicantLoading,
  filters,
  setFilters,
  branches,
  hideBranchFilter,
  jobs,
  onApplyFilters,
}) {
  return (
    <Paper sx={{ p: 2 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          select
          label='Status'
          size='small'
          value={filters.status}
          onChange={event => setFilters(current => ({ ...current, status: event.target.value }))}
          sx={{ minWidth: 180 }}
        >
          {APPLICANT_STATUS_OPTIONS.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        {!hideBranchFilter && (
          <TextField
            select
            label='Standort'
            size='small'
            value={filters.branchId}
            onChange={event => setFilters(current => ({ ...current, branchId: event.target.value }))}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value=''>Alle Standorte</MenuItem>
            {branches.map(branch => (
              <MenuItem key={branch._id} value={branch._id}>
                {getBranchLabel(branch)}
              </MenuItem>
            ))}
          </TextField>
        )}
        <TextField
          select
          label='Ausgeschriebene Stelle'
          size='small'
          value={filters.jobId}
          onChange={event => setFilters(current => ({ ...current, jobId: event.target.value }))}
          sx={{ minWidth: 280 }}
        >
          <MenuItem value=''>Alle Stellen</MenuItem>
          {jobs.map(job => (
            <MenuItem key={job.id} value={job.id}>
              {job.title || job.id}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label='Suche'
          size='small'
          value={filters.search}
          onChange={event => setFilters(current => ({ ...current, search: event.target.value }))}
          placeholder='Name, E-Mail, Job oder Stage'
          sx={{ minWidth: 280, flex: 1 }}
        />
        <Button variant='outlined' onClick={onApplyFilters} disabled={applicantLoading}>
          Filter anwenden
        </Button>
      </Stack>

      {overdueApplicants.length > 0 && filters.status !== 'overdue' && (
        <Alert severity='error' sx={{ mb: 2 }}>
          Wichtig: {overdueApplicants.length} Bewerber in der aktuellen Ansicht sind überfällig.
        </Alert>
      )}

      {applicantLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>Bewerber</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Stage</TableCell>
                <TableCell>In Phase</TableCell>
                <TableCell>Job</TableCell>
                <TableCell align='right'>Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {applicants.map(applicant => (
                <TableRow
                  key={applicant._id}
                  hover
                  sx={{
                    borderLeft: theme =>
                      `4px solid ${
                        applicant.isOverdue ? theme.palette.error.main : theme.palette.divider
                      }`,
                    bgcolor: applicant.isOverdue ? 'error.lighter' : undefined,
                  }}
                >
                  <TableCell>
                    <Typography variant='body2' sx={{ fontWeight: 600 }}>
                      {applicant.candidateName}
                    </Typography>
                    <Stack spacing={0.25} sx={{ mt: 0.25 }}>
                      <Typography variant='caption' color='text.secondary'>
                        {(applicant.emails || []).join(', ') || 'Keine E-Mail'}
                      </Typography>
                      <Typography variant='caption' color='text.secondary'>
                        {(applicant.phones || []).join(', ') || 'Keine Telefonnummer'}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack spacing={0.5} alignItems='flex-start'>
                      {statusChip(applicant)}
                      {applicant.isOverdue && applicant.overdueDays > 0 && (
                        <Typography variant='caption' color='error.main'>
                          {applicant.overdueDays} Tage überfällig
                        </Typography>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant='body2'>{applicant.stageName || '-'}</Typography>
                    <Typography variant='caption' color='text.secondary'>
                      {applicant.stageCategory || ''}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant='body2'>
                      {formatPhaseDuration(getPhaseStartedAt(applicant))}
                    </Typography>
                    <Typography variant='caption' color='text.secondary'>
                      seit {formatDate(getPhaseStartedAt(applicant))}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant='body2'>{applicant.jobTitle}</Typography>
                    <Typography variant='caption' color='text.secondary'>
                      {applicant.collectionName || 'Ohne Collection'}
                    </Typography>
                  </TableCell>
                  <TableCell align='right'>
                    {applicant.adminappUrl && (
                      <Button
                        size='small'
                        component='a'
                        href={applicant.adminappUrl}
                        target='_blank'
                        rel='noopener noreferrer'
                        sx={{
                          color: '#5a1cb7',
                          borderColor: '#5a1cb7',
                          whiteSpace: 'nowrap',
                          '&:hover': {
                            borderColor: '#5a1cb7',
                            bgcolor: 'rgba(90, 28, 183, 0.08)',
                          },
                        }}
                        variant='outlined'
                      >
                        zu Recruitee
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {applicants.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align='center'>
                    <Typography color='text.secondary' sx={{ py: 4 }}>
                      Keine Bewerber gefunden.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}

function MappingsTab({
  mappings,
  canManage,
  canSync,
  syncing,
  onAdd,
  onEdit,
  onDelete,
  onSync,
}) {
  return (
    <Paper sx={{ p: 2 }}>
      <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{ mb: 2 }}>
        <Box>
          <Typography variant='h6'>Rolle/Standort zu Recruitee-Job</Typography>
          <Typography variant='body2' color='text.secondary'>
            Diese Zuordnung bestimmt, welche Bewerber in welchem Standort- und Rollen-Kontext
            angezeigt werden.
          </Typography>
        </Box>
        {canManage && (
          <Button variant='contained' startIcon={<AddIcon />} onClick={onAdd}>
            Neues Mapping
          </Button>
        )}
      </Stack>

      <TableContainer>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>Standort</TableCell>
              <TableCell>Rolle</TableCell>
              <TableCell>Collection</TableCell>
              <TableCell>Job</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Letzter Sync</TableCell>
              {(canManage || canSync) && <TableCell align='right'>Aktionen</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {mappings.map(mapping => (
              <TableRow key={mapping._id} hover>
                <TableCell>{getBranchLabel(mapping.branchId)}</TableCell>
                <TableCell>{getRoleLabel(mapping.roleId)}</TableCell>
                <TableCell>{mapping.collectionName || 'Ohne Collection'}</TableCell>
                <TableCell>
                  <Typography variant='body2'>{mapping.jobTitle}</Typography>
                  <Typography variant='caption' color='text.secondary'>
                    #{mapping.jobId}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    size='small'
                    color={mapping.enabled ? 'success' : 'default'}
                    label={mapping.enabled ? 'Aktiv' : 'Inaktiv'}
                  />
                </TableCell>
                <TableCell>{formatDate(mapping.lastSyncedAt)}</TableCell>
                {(canManage || canSync) && (
                  <TableCell align='right'>
                    {canSync && (
                      <Tooltip title='Dieses Mapping synchronisieren'>
                        <IconButton size='small' onClick={() => onSync(mapping._id)} disabled={syncing}>
                          <SyncIcon fontSize='small' />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canManage && (
                      <>
                        <Tooltip title='Bearbeiten'>
                          <IconButton size='small' onClick={() => onEdit(mapping)}>
                            <EditIcon fontSize='small' />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title='Löschen'>
                          <IconButton size='small' color='error' onClick={() => onDelete(mapping)}>
                            <DeleteIcon fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
            {mappings.length === 0 && (
              <TableRow>
                <TableCell colSpan={canManage || canSync ? 7 : 6} align='center'>
                  <Typography color='text.secondary' sx={{ py: 4 }}>
                    Noch keine Recruitee-Mappings vorhanden.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

function MappingDialog({ mapping, branches, roles, collections, jobs, saving, onCancel, onSave }) {
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [enabled, setEnabled] = useState(true);

  const branchOptions = useMemo(
    () => branches.map(branch => ({ ...branch, label: getBranchLabel(branch) })),
    [branches]
  );
  const roleOptions = useMemo(
    () => roles.map(role => ({ ...role, label: getRoleLabel(role) })),
    [roles]
  );
  const collectionOptions = useMemo(
    () => collections.map(collection => ({ ...collection, label: collection.name || collection.id })),
    [collections]
  );
  const jobOptions = useMemo(
    () =>
      jobs.map(job => ({
        ...job,
        label: `${job.title || job.id}${job.collectionName ? ` · ${job.collectionName}` : ''}`,
      })),
    [jobs]
  );
  const filteredJobOptions = useMemo(() => {
    if (!selectedCollection?.id) return jobOptions;
    return jobOptions.filter(job => String(job.collectionId || '') === String(selectedCollection.id));
  }, [jobOptions, selectedCollection]);

  useEffect(() => {
    if (!mapping) return;
    setSelectedBranch(
      branchOptions.find(branch => String(branch._id) === String(mapping.branchId?._id || mapping.branchId)) ||
        null
    );
    setSelectedRole(
      roleOptions.find(role => String(role._id) === String(mapping.roleId?._id || mapping.roleId)) || null
    );
    setSelectedCollection(
      collectionOptions.find(collection => String(collection.id) === String(mapping.collectionId || '')) ||
        null
    );
    setSelectedJob(
      jobOptions.find(job => String(job.id) === String(mapping.jobId)) || {
        id: mapping.jobId,
        title: mapping.jobTitle,
        label: mapping.jobTitle,
        collectionId: mapping.collectionId,
        collectionName: mapping.collectionName,
        status: mapping.jobStatus,
        kind: mapping.jobKind,
      }
    );
    setEnabled(mapping.enabled !== false);
  }, [branchOptions, collectionOptions, jobOptions, mapping, roleOptions]);

  const handleSubmit = () => {
    if (!selectedBranch || !selectedRole || !selectedJob) return;
    onSave({
      branchId: selectedBranch._id,
      roleId: selectedRole._id,
      collectionId: selectedJob.collectionId || selectedCollection?.id || '',
      collectionName: selectedJob.collectionName || selectedCollection?.name || '',
      jobId: selectedJob.id,
      jobTitle: selectedJob.title || selectedJob.label,
      jobStatus: selectedJob.status || '',
      jobKind: selectedJob.kind || '',
      enabled,
    });
  };

  return (
    <Dialog open onClose={onCancel} maxWidth='md' fullWidth>
      <DialogTitle>{mapping ? 'Recruitee-Mapping bearbeiten' : 'Neues Recruitee-Mapping'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Autocomplete
            options={branchOptions}
            value={selectedBranch}
            onChange={(_, value) => setSelectedBranch(value)}
            getOptionLabel={option => option.label || ''}
            isOptionEqualToValue={(option, value) => String(option._id) === String(value?._id)}
            renderInput={params => <TextField {...params} label='Standort' required />}
          />
          <Autocomplete
            options={roleOptions}
            value={selectedRole}
            onChange={(_, value) => setSelectedRole(value)}
            getOptionLabel={option => option.label || ''}
            isOptionEqualToValue={(option, value) => String(option._id) === String(value?._id)}
            renderInput={params => <TextField {...params} label='Rolle' required />}
          />
          <Autocomplete
            options={collectionOptions}
            value={selectedCollection}
            onChange={(_, value) => {
              setSelectedCollection(value);
              setSelectedJob(null);
            }}
            getOptionLabel={option => option.label || ''}
            isOptionEqualToValue={(option, value) => String(option.id) === String(value?.id)}
            renderInput={params => (
              <TextField {...params} label='Collection' helperText='Leer lassen zeigt alle Jobs' />
            )}
          />
          <Autocomplete
            options={filteredJobOptions}
            value={selectedJob}
            onChange={(_, value) => setSelectedJob(value)}
            getOptionLabel={option => option.label || option.title || ''}
            isOptionEqualToValue={(option, value) => String(option.id) === String(value?.id)}
            renderInput={params => <TextField {...params} label='Recruitee-Job' required />}
          />
          <FormControlLabel
            control={<Checkbox checked={enabled} onChange={event => setEnabled(event.target.checked)} />}
            label='Mapping aktiv'
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Abbrechen</Button>
        <Button
          variant='contained'
          onClick={handleSubmit}
          disabled={!selectedBranch || !selectedRole || !selectedJob || saving}
          startIcon={saving ? <CircularProgress size={16} /> : undefined}
        >
          Speichern
        </Button>
      </DialogActions>
    </Dialog>
  );
}
